(ns common.schema.entities.investor.schema
  (:require
   [clojure.string :as string]
   [common.currencies :as currencies]
   [common.schema.entities.kyc.options :as kyc-options]
   [common.languages :as languages]
   [common.schema.entities.customer :as customer]
   [common.schema.option-filters :as option-filters]
   [common.schema.schema-utils :as su]
   [common.util :as cu]))

(def import-fixed-fields [:investor/name])

(def schema
  (vec
   (concat
    customer/schema
    (su/text-simple-field
     :investor/name
     "Name"
     {:editor-type              :editor.type/link
      :default-field-visibility :in-use-disabled
      :name-attribute           :investor/name
      :system-required?         true
      :system-semantics
      su/user-provided-system-required-field-semantics
      :mandatory-config         :mandatory/always
      :lower-case-str?          true
      :not-clearable?           true
      :constraints              {:constraint/string-min-length 1}})
    ;; Potential future development:
    ;; derive investor display naming from :investor/legal-entity instead of storing :investor/name directly.
    #_(su/calculated-field
       :investor/investor-name
       "Name"
       {:data-type                :data.type/text-simple
        :name-attribute           :investor/investor-name
        :composite-attrs          [:investor/legal-entity]
        :calculation-triggers     {:entity.type/investor [[:investor/legal-entity]]
                                   :entity.type/person [[:investor/_legal-entity]]
                                   :entity.type/company [[:investor/_legal-entity]]}
        :default-field-visibility :in-use-disabled
        :editor                   :editor.type/link
        :grid                     :editor.type/link
        :calc-fn                  investor-name-from-legal-entity})
    (su/single-ref-field :investor/legal-entity
                         "Legal entity"
                         [:entity.type/company :entity.type/person]
                         {:create-on-fly?             true
                          :deep-indexing?             true
                          :system-required?           true
                          :system-semantics
                          su/user-provided-system-required-field-semantics
                          :mandatory-config           :mandatory/always
                          :default-field-visibility   :in-use-disabled
                          :hidden-from-configuration? true
                          :not-clearable?             true
                          :editor-type                :editor.type/select
                          :non-empty-editor-type      :editor.type/link
                          :grid-type                  :editor.type/link})
    (su/text-multiline-field :investor/description
                             "Description"
                             {:default-field-visibility :in-use})
    (su/text-multiline-field :investor/note "Note" {})
    (su/text-simple-field :investor/lei-id "LEI code" {})
    (su/text-simple-field :investor/tax-id "Tax id" {})
    (su/option-field-multi :investor/tax-status "Tax status" {})
    (su/option-field-multi :investor/focus-stage
                           "Focus - stage"
                           {:default-field-visibility :in-use})
    (su/option-field-multi :investor/insider "Insider" {})
    (su/option-field-multi :investor/focus-sector
                           "Focus - sector"
                           {:default-field-visibility :in-use})
    (su/option-field-multi :investor/focus-geography
                           "Focus - geography"
                           {:default-field-visibility :in-use})
    (su/option-field-multi :investor/co-invest-interest "Co-investment interest" {})
    (su/option-field :investor/investor-type "Investor type" {:default-field-visibility :in-use})
    (su/option-field-multi :investor/investor-types "Investor types" {})
    (su/url-field :investor/deal-room "Deal room" {:default-field-visibility :not-in-use-blocked})
    (su/option-field :investor/family "Family" {})
    (su/option-field :investor/domicile "Domicile" {})
    (su/option-field-multi :investor/type "Type" {:default-field-visibility :in-use})
    (su/option-field :investor/typical-investment-range
                     "Typical investment range"
                     {:default-field-visibility :in-use})
    (su/currency-field :investor/cur-typical-investment-min
                       "Typical investment minimum"
                       {:default-field-visibility :in-use})
    (su/currency-field :investor/cur-typical-investment-max
                       "Typical investment maximum"
                       {:default-field-visibility :in-use})
    (su/currency-field :investor/cur-AUM "AUM" {:default-field-visibility :in-use})
    (su/option-field-multi :investor/tag-tags "Investor tags" {:default-field-visibility :in-use})
    (su/option-field-multi :investor/interest-in-products
                           "Interest in products"
                           {:default-field-visibility :in-use})
    (su/keyword-field :investor/kyc-status
                      "KYC status"
                      kyc-options/status-options
                      {:default-field-visibility :not-in-use
                       :authorization            su/access-user-user})
    (su/keyword-field :investor/language
                      "Investor language"
                      languages/options
                      {:default-field-visibility :not-in-use})
    (su/option-field :investor/customer-category "Customer category" {})
    (su/option-field :investor/sideletter "Side letter" {})
    (su/single-ref-field :investor/cws-owner
                         "CWS owner"
                         [:entity.type/account]
                         {:option-filter-fn         option-filters/active-users
                          :os-filter-terms-fn       (fn [{:keys [] :as _args}]
                                                      {"extras.active-user" true})
                          :default-field-visibility :not-in-use-blocked})

    (su/date-field :investor/date-liquidity-window-start-date
                   "Liquidity window start date"
                   {:default-field-visibility :not-in-use-blocked})
    (su/date-field :investor/date-liquidity-window-end-date
                   "Liquidity window end date"
                   {:default-field-visibility :not-in-use-blocked})

    [[:investor/number-of-active-agreements
      {:field/data-type             :data.type/integer
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/integer
                                     :filter           :filter.type/integer
                                     :field-visibility :in-use}
       :field/default-authorization {:access/read  :role/user
                                     :access/write :role/system}
       :field/system-semantics      su/system-controlled-optional-field-semantics
       :calculation-triggers        {:entity.type/sale-agreement [[:sale-agreement/customer]]}
       :field/composite-attributes  [:sale-agreement/customer :sale-agreement/contract-end-date]    ;; :db/id ?
       :field/calculated-field      (fn [{:keys [_logger entity-data entity-lookup reverse-lookup _tr _bigdec->float _get-field-schema
                                                 _display-string]}]
                                      (let [agreements (reverse-lookup
                                                        {:entity                 entity-data
                                                         :attribute-entity-types [:entity.type/sale-agreement]
                                                         :attribute              :sale-agreement/customer})
                                            active-agreements (->> agreements
                                                                   (keep entity-lookup)
                                                                   (remove :common/disabled?)
                                                                   (remove :sale-agreement/contract-end-date))]
                                        (count active-agreements)))
       :field/translations          {:en "Number of active agreements"}}]

     [:investor/num-active-sales-oppos
      {:field/data-type             :data.type/integer
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/integer
                                     :filter           :filter.type/integer
                                     :field-visibility :in-use}
       :field/default-authorization {:access/read  :role/user
                                     :access/write :role/system}
       :field/system-semantics      su/system-controlled-optional-field-semantics
       :field/composite-attributes  [:sales-opportunity/customer :sales-opportunity/status]
       :calculation-triggers        {:entity.type/sales-opportunity [[:sales-opportunity/customer]]}
       :field/calculated-field      (fn [{:keys [_logger entity-data entity-lookup reverse-lookup _tr _bigdec->float _get-field-schema
                                                 _display-string]}]
                                      (let [oppos (reverse-lookup
                                                   {:entity                 entity-data
                                                    :attribute-entity-types [:entity.type/sales-opportunity]
                                                    :attribute              :sales-opportunity/customer})
                                            active-sales-oppo-count (->> oppos
                                                                         (keep entity-lookup)
                                                                         (remove :common/disabled?)
                                                                         (map :sales-opportunity/status)
                                                                         (filter (partial = :sales-opportunity.status/active))
                                                                         (count))]
                                        active-sales-oppo-count))
       :field/translations          {:en "Number of active deals in sales pipelines"}}]

     [:investor/preferred-currency
      {:field/default-authorization {:access/read  :role/user
                                     :access/write :role/user}
       :field/data-type             :data.type/currency-symbol
       :options                     currencies/options
       :field/default-visibility    {:editor           :editor.type/select
                                     :grid             :editor.type/select
                                     :filter           :filter.type/select
                                     :field-visibility :in-use}
       :value-type                  :keyword
       :field/translations          {:en "Preferred investing currency"}}]

     [:investor/status
      {:field/data-type             :data.type/ref-multi
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :investor/status
       :field/default-visibility    {:editor           :editor.type/multi-select
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :in-use}
       :field/default-authorization {:access/read   :role/user
                                     :access/create :role/admin
                                     :access/write  :role/user}
       :field/translations          {:en "Investor status"}}]

     [:investor/allocation-split
      {:field/data-type             :data.type/text-edn
       :field/default-visibility    {:editor           :editor.type/allocation-split
                                     :grid             :editor.type/none
                                     :filter           :filter.type/select
                                     :field-visibility :in-use}
       :field/default-authorization {:access/read   :role/user
                                     :access/create :role/admin
                                     :access/write  :role/user}
       :field/not-importable?       true
       :field/translations          {:en "Allocation split"}}]]
    (su/date-field :investor/date-last-meeting-date
                   "Last meeting date"
                   {:default-field-visibility :not-in-use})
    (su/date-field :investor/date-next-meeting-date
                   "Next meeting date"
                   {:default-field-visibility :not-in-use})
    (su/option-field :investor/source "Source" {:default-field-visibility :not-in-use})
    (su/single-ref-field :investor/owner
                         "Owner"
                         [:entity.type/account]
                         {:option-filter-fn         option-filters/active-users
                          :os-filter-terms-fn       (fn [{:keys [] :as _args}]
                                                      {"extras.active-user" true})
                          :default-field-visibility :not-in-use})
    (su/integer-field :investor/hotels
                      "Hotels"
                      {:default-field-visibility :not-in-use-blocked})
    (su/calculated-field :investor/estimated-number-of-keys
                         "Keys"
                         {:default-field-visibility :not-in-use-blocked
                          :data-type                :data.type/integer
                          :calc-fn                  (fn [{:keys [entity-data]}]
                                                      (if-let [hotels (:investor/hotels entity-data)]
                                                        (* hotels 150)
                                                        0))})
    (su/calculated-field :investor/number-of-active-holdings
                         "Number of holdings where active investor"
                         {:default-field-visibility :not-in-use-blocked
                          :data-type                :data.type/integer
                          :calculation-triggers     {:entity.type/company   [[:investor/_legal-entity]]
                                                     :entity.type/fof-asset [[:fof-asset/gp :investor/_legal-entity]]
                                                     :entity.type/holding   [[:holding/target-asset :fof-asset/gp :investor/_legal-entity]]}
                          :calc-fn                  (fn [{:keys [_logger entity-data entity-lookup reverse-lookup
                                                                 _tr _bigdec->float
                                                                 _get-field-schema _display-string]}]
                                                      (let [company (entity-lookup (:investor/legal-entity entity-data))
                                                            fofs-where-gp (reverse-lookup
                                                                           {:entity                 company
                                                                            :attribute-entity-types [:entity.type/fof-asset]
                                                                            :attribute              :fof-asset/gp})
                                                            fof-holdings (->> fofs-where-gp
                                                                              (keep entity-lookup)
                                                                              (remove :common/disabled?)
                                                                              (mapcat (fn [fof-data]
                                                                                        (reverse-lookup
                                                                                         {:entity                 fof-data
                                                                                          :attribute-entity-types [:entity.type/holding]
                                                                                          :attribute              :holding/target-asset}))))
                                                            active-holdings (->> fof-holdings
                                                                                 (keep entity-lookup)
                                                                                 (remove :common/disabled?)
                                                                                 (cu/remove-by :holding/exit-status :holding.exit-status/exited))]
                                                        (count active-holdings)))})

    (su/calculated-field
     :investor/exit-opportunities-where-buyer
     "Exit-opportunities where buyer-candidate"
     {:default-field-visibility :in-use
      :data-type                :data.type/ref-multi
      :value-type               :entity
      :entity-types             [:entity.type/exit-opportunity]
      :required-modules         [:module/exit-process-management]
      :calculation-triggers     {:entity.type/exit-opportunity [[:exit-opportunity/buyer]]}
      :calc-fn
      (fn [{:keys [_logger entity-data entity-lookup reverse-lookup
                   _tr _bigdec->float
                   _get-field-schema _display-string]}]
        (let [exit-oppos (reverse-lookup
                          {:entity                 entity-data
                           :attribute-entity-types [:entity.type/exit-opportunity]
                           :attribute              :exit-opportunity/buyer})]
          (->> exit-oppos
               (keep entity-lookup)
               (remove :common/disabled?)
               (mapv :db/id)
               not-empty)))})

    (su/calculated-field
     :investor/active-in-funds
     "Active in funds"
     {:default-field-visibility :not-in-use-blocked
      :data-type                :data.type/text-simple
      :calculation-triggers     {:entity.type/fund-commitment [[:fund-commitment/investor]]
                                 :entity.type/fund            [[:fund-commitment/_fund :fund-commitment/investor]]}
      ;; NOTE: `:composite-attrs` declares the user-permission gate (see
      ;; `common.permissions.account-permissions/composite-attrs-accessible?`).
      ;; That gate has a latent bug when the parent calc-field lives on a
      ;; CONFIGURABLE_ENTITY (e.g. :entity.type/investor) and any composite
      ;; attribute lives on a different entity-type: the cached
      ;; `account-current-permissions` map is keyed strictly by entity-type,
      ;; so cross-entity composites silently resolve to nil and the column
      ;; is hidden from every non-zadmin user. Until that gate is fixed
      ;; properly, leaving these out matches the working sibling
      ;; `:investor/exit-opportunities-where-buyer` which also declares no
      ;; composite-attrs. Backend recompute is driven by
      ;; `:calculation-triggers` above, not by this list, so dropping it
      ;; does not affect reactivity.
      #_:composite-attrs        #_[:fund-commitment/fund :fund-commitment/investor
                                 :fund-commitment/exit-date :fund/external-kyc-tool :fund/name
                                 :fund/date-liquidation-date]
      :calc-fn                  (fn [{:keys [entity-data entity-lookup reverse-lookup]}]
                                  (let [commitments (reverse-lookup
                                                     {:entity                 entity-data
                                                      :attribute-entity-types [:entity.type/fund-commitment]
                                                      :attribute              :fund-commitment/investor})
                                        active-commitments (->> commitments
                                                                (keep entity-lookup)
                                                                (remove :common/disabled?)
                                                                (remove :fund-commitment/exit-date))
                                        active-funds (->> active-commitments
                                                          (map :fund-commitment/fund)
                                                          (distinct)
                                                          (keep entity-lookup)
                                                          (remove :common/disabled?)
                                                          (remove :fund/date-liquidation-date)
                                                          ;; Mirror the KYC profile behaviour: exclude
                                                          ;; external-kyc-tool funds so they only show
                                                          ;; up in the administrator-funds column.
                                                          (remove :fund/external-kyc-tool))]
                                    (->> active-funds
                                         (map :fund/name)
                                         (string/join "; "))))})

    (su/calculated-field
     :investor/active-in-administrator-funds
     "Active in administrator funds"
     {:default-field-visibility :not-in-use-blocked
      :data-type                :data.type/text-simple
      :calculation-triggers     {:entity.type/fund-commitment [[:fund-commitment/investor]]
                                 :entity.type/fund            [[:fund-commitment/_fund :fund-commitment/investor]]}
      ;; See note on `:investor/active-in-funds` above re: the latent permission-gate
      ;; bug that hides calc-fields with cross-entity composite-attrs from non-zadmin users.
      #_:composite-attrs        #_[:fund-commitment/fund :fund-commitment/investor
                                 :fund-commitment/exit-date :fund/external-kyc-tool :fund/name]
      :calc-fn                  (fn [{:keys [entity-data entity-lookup reverse-lookup]}]
                                  (let [commitments (reverse-lookup
                                                     {:entity                 entity-data
                                                      :attribute-entity-types [:entity.type/fund-commitment]
                                                      :attribute              :fund-commitment/investor})
                                        active-commitments (->> commitments
                                                                (keep entity-lookup)
                                                                (remove :common/disabled?)
                                                                (remove :fund-commitment/exit-date))
                                        administrator-funds (->> active-commitments
                                                                 (map :fund-commitment/fund)
                                                                 (distinct)
                                                                 (keep entity-lookup)
                                                                 (remove :common/disabled?)
                                                                 (remove :fund/date-liquidation-date)
                                                                 (filter :fund/external-kyc-tool))]
                                    (->> administrator-funds
                                         (map :fund/name)
                                         (string/join "; "))))})

    (su/multi-ref-field :investor/risk-entities
                        "Risks"
                        [:entity.type/risk-holding]
                        {:authorization    su/access-user-user
                         :editor-type      :editor.type/none
                         :grid-type        :editor.type/none
                         :filter-type      :filter.type/boolean
                         :required-modules [:module/risk-module]}))))
